/**
 * A docs page's content: the manifest entry, its markdown fetched from the
 * served `/content/` tree and rendered, and the related pages. It is a
 * resource keyed by route, so revisiting a page shows it at once while it is
 * checked again.
 */
import { pageUrl } from '@crown/framework/context';
import { resource, type Resource } from '@crown/framework/data';
import {
  type DocEntry,
  findDocByRoute,
  getRelatedDocs,
} from '../content/content-manifest';
import { renderMarkdown } from '../content/markdown';

export interface DocPageData {
  readonly entry: DocEntry;
  /** The page's markdown as HTML; the renderer escapes every piece of text it writes. */
  readonly html: string;
  readonly relatedDocs: readonly DocEntry[];
}

export async function loadDoc(routePath: string): Promise<DocPageData> {
  const entry = findDocByRoute(routePath);
  if (!entry) throw new Error(`Unknown docs route: ${routePath}`);
  // Against the page's own address, so the server rendering it asks itself.
  const response = await fetch(
    new URL(`/content/${entry.contentPath}`, pageUrl()),
  );
  if (!response.ok)
    throw new Error(`Failed to load docs content: ${entry.contentPath}`);
  const markdown = await response.text();
  return {
    entry,
    html: renderMarkdown(markdown, entry.contentPath),
    relatedDocs: getRelatedDocs(entry),
  };
}

export function docResource(routePath: string): Resource<DocPageData> {
  return resource(
    () => ['docs', routePath] as const,
    ([, path]) => loadDoc(path),
  );
}
